import { useState, useEffect } from "react";
import { DOCUMENT_ACCEPT, DOCUMENT_ACCEPT_LABEL } from "@/utils/uploadFileTypes";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, FileText, X } from "lucide-react";
import {
  normalizeDocAttachmentLabel,
  type AttachmentLabel,
} from "@/utils/attachmentLabels";

interface EditDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (
    name: string,
    file: File | null,
    attachmentLabel: AttachmentLabel
  ) => void;
  doc: any;
  isPending?: boolean;
  isRequired?: boolean;
}

export default function EditDocumentModal({
  isOpen,
  onClose,
  onSave,
  doc,
  isPending = false,
  isRequired = false,
}: EditDocumentModalProps) {
  const [name, setName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [attachmentLabel, setAttachmentLabel] =
    useState<AttachmentLabel>("WORKFILE");

  useEffect(() => {
    if (doc) {
      setName(doc.name || doc.fileName?.split("/").pop() || "");
      setFile(null);
      setAttachmentLabel(normalizeDocAttachmentLabel(doc.attachmentLabel));
    }
  }, [doc, isOpen]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  const handleSave = () => {
    onSave(name, file, attachmentLabel);
  };

  const labelDirty =
    doc &&
    attachmentLabel !== normalizeDocAttachmentLabel(doc.attachmentLabel);
  const canSave =
    Boolean(name?.trim()) || file != null || Boolean(labelDirty);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[425px] rounded-xl">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold text-[#051912]">
            Edit Document
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="doc-name" className="text-sm font-medium">
              Document Name
            </Label>
            <Input
              id="doc-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter document name"
              disabled={isRequired}
              className={isRequired ? "opacity-60 cursor-not-allowed" : ""}
            />
            {isRequired && (
              <p className="text-xs text-blue-600">
                This is a required document. The name cannot be changed.
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Label className="text-sm font-medium">Document type</Label>
            <div className="flex flex-wrap gap-2">
              {(
                [
                  { v: "WORKFILE" as const, t: "Work file" },
                  { v: "PAYMENT_RECEIPT" as const, t: "Payment receipt" },
                ] as const
              ).map(({ v, t }) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setAttachmentLabel(v)}
                  className={`h-8 px-3 rounded-md text-xs font-medium border transition-colors ${
                    attachmentLabel === v
                      ? "bg-[#1e302a] text-white border-[#1e302a]"
                      : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label className="text-sm font-medium">
              Replace File (Optional)
            </Label>

            {file ? (
              <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
                <div className="flex items-center gap-2 overflow-hidden">
                  <FileText className="size-4 text-blue-500 shrink-0" />
                  <span className="text-sm truncate">{file.name}</span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-6 text-gray-500 hover:text-red-500"
                  onClick={() => setFile(null)}
                >
                  <X className="size-4" />
                </Button>
              </div>
            ) : (
              <div className="relative">
                <Input
                  type="file"
                  id="file-replace"
                  className="hidden"
                  onChange={handleFileChange}
                  accept={DOCUMENT_ACCEPT}
                />
                <Label
                  htmlFor="file-replace"
                  className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <Upload className="size-6 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">
                    Click to browse or drag file here
                  </span>
                  <span className="text-xs text-gray-400 mt-1">
                    {DOCUMENT_ACCEPT_LABEL} up to 5MB
                  </span>
                </Label>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="cancel" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={isPending || !canSave}
          >
            {isPending ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
